import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    CircularProgress,
    Alert,
} from '@mui/material';
import api from '../api/api';
import { useTranslation } from 'react-i18next';

function EmailVerificationPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // 1. Create a ref to track if the effect has already run
    const hasCalledApi = useRef(false);

    useEffect(() => {
        const token = searchParams.get('token');

        if (!token) {
            setError(t('common.invalidToken'));
            setLoading(false);
            return;
        }

        // 2. Check the ref before making the call
        if (hasCalledApi.current) {
            // If true, skip the API call (this catches the second execution in Strict Mode)
            return;
        }

        // 3. Set the ref to true immediately before starting the process
        hasCalledApi.current = true;

        const verifyEmail = async () => {
            try {
                // Ensure loading is set for the visual feedback
                setLoading(true);
                
                // The BE endpoint is /user/verify?token=...
                await api.post(`/user/verify?token=${token}`);

                // On success (204), redirect to login after a brief delay
                setTimeout(() => {
                    navigate('/login', { state: { message: t('common.emailVerifiedSuccess') } });
                }, 3000); // 3-second delay
            } catch (err) {
                // On error, show a generic error message
                const errorMessage = err.response?.data?.message || err.message;
                setError(t('common.errorOccurred', { message: errorMessage }));
            } finally {
                setLoading(false);
            }
        };

        verifyEmail();

    // The dependency array remains the same
    }, [searchParams, navigate, t]); 

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Box textAlign="center">
                <Typography variant="h4" component="h1" gutterBottom>
                    {t('common.emailVerification')}
                </Typography>
                {loading && (
                    <Box sx={{ mt: 4 }}>
                        <CircularProgress />
                        <Typography variant="body1" sx={{ mt: 2 }}>
                            {t('common.verifyingEmail')}
                        </Typography>
                    </Box>
                )}
                {!loading && error && (
                    <Alert severity="error" sx={{ mt: 4 }}>
                        {error}
                    </Alert>
                )}
                {!loading && !error && (
                    <Alert severity="success" sx={{ mt: 4 }}>
                        {t('common.emailVerifiedSuccess')}
                        <br />
                        {t('common.redirectingToLogin')}
                    </Alert>
                )}
            </Box>
        </Container>
    );
}

export default EmailVerificationPage;